<template>
  <div v-if="isInitialized">
    <v-img
      :src="imageUrl"
      :lazy-src="imageUrlLowRes"
      max-height="200"
      gradient="to top right, rgba(100,115,201,.33), rgba(25,32,72,.7)"
    >
      <h1 class="ma-3">{{ art.name_sve }}</h1>
    </v-img>

    <div class="tabs-row mt-3 mb-7">
      <div>
        <v-tabs center-active>
          <v-tab v-for="tab in tabs" :key="tab.id" :to="tab.route">{{ tab.title }}</v-tab>
        </v-tabs>
      </div>
      <div class="buttons">
        <v-btn text-color="white" :color="colors[art.status]" @click="onArtPublish"
          >Arten är {{ art.status === 'online' ? 'online' : 'offline' }}</v-btn
        >
        <v-btn
          elevation="2"
          color="primary"
          class="mb-2"
          @click="save"
          :disabled="!hasUnsavedUpdates"
          :loading="isSaving"
          >Spara</v-btn
        >
        <CommonMenu :items="[{ key: 'remove', value: 'Radera arten' }]" @remove="onRemove" />
      </div>
    </div>
    <router-view
      v-bind="art"
      :loading="loading || isSaving"
      :environments="environments"
      :geo="geoAreas"
      :links="links"
      :images="images"
      :facts="facts"
      @change="onUpdate"
      @change:images="onUpdateImages"
      @change:facts="onUpdateFacts"
      @change:links="onUpdateLinks"
      @change:links:order="onUpdateLinksOrder"
    />
    <Message ref="message" />
    <Confirm ref="confirm" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { colors } from '@/assets/data/colors'
import Message from '@/components/shared/Message'
import Spinner from '@/components/shared/Spinner'
import Confirm from '@/components/shared/Confirm'
import CommonMenu from '@/components/shared/CommonMenu'

export default {
  name: 'Art',
  props: { id: { type: [String, Number] } },
  components: {
    Message,
    Spinner,
    Confirm,
    CommonMenu,
  },
  data() {
    return {
      loading: true,
      isInitialized: false,
      colors,
      activeTab: 0,
      tabs: [
        { id: 0, title: 'Allmänt', route: { name: 'art-info-general' } },
        { id: 1, title: 'Arten', route: { name: 'art-info-base' } },
        { id: 2, title: 'Miljöer och områden', route: { name: 'art-info-geo-env' } },
        { id: 3, title: 'Länkar', route: { name: 'art-info-links' } },
        { id: 4, title: 'Bilder & Filmer', route: { name: 'art-info-bilder' } },
        { id: 5, title: 'Fakta', route: { name: 'art-info-facts' } },
      ],
      updates: {},
      isSaving: false,
    }
  },
  computed: {
    ...mapGetters('arter', { getArtById: 'getById' }),
    hasUnsavedUpdates() {
      return !!Object.keys({ ...this.updates, ...this.updatesContent }).length
    },
    art() {
      return this.getArtById(+this.id)
    },
    environments() {
      return 'environments' in this.updates ? this.updates.environments : this.art.environments
    },
    geoAreas() {
      return 'geoAreas' in this.updates ? this.updates.geoAreas : this.art.geoAreas
    },
    links() {
      return this.art.links
    },
    images() {
      return [...this.art.images].sort((a, b) => a.sort - b.sort)
    },
    facts() {
      return this.art.facts
    },
    imageUrl() {
      if (this.images.length === 0) return null
      return `${process.env.VUE_APP_IMAGE_BACKEND}/870/${this.images[0].li_image_id}.jpg`
    },
    imageUrlLowRes() {
      if (this.images.length === 0) return null
      return `${process.env.VUE_APP_IMAGE_BACKEND}/170/${this.images[0].li_image_id}.jpg`
    },
  },
  methods: {
    ...mapActions('arter', {
      getSingleArt: 'read',
      updateArt: 'update',
      deleteArt: 'delete',
    }),
    async fetch() {
      await this.getSingleArt({ id: +this.id })
      this.isInitialized = true
      this.loading = false
    },
    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort arten',
        message: 'Är du säker på att du vill radera arten?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return

      await this.deleteArt({ ...this.art })
      this.$router.replace({ name: 'ArterList' })
    },
    onUpdate(payload) {
      this.updates = { ...this.updates, ...payload }
    },
    onUpdateFacts(factIds) {
      const payload = {
        facts: factIds.map((id) => ({
          spec_id: +this.id,
          fact_id: id,
        })),
      }
      this.saveImmediately(payload)
    },
    onUpdateImages(payload) {
      this.saveImmediately(payload)
    },
    onUpdateLinks(payload) {
      this.saveImmediately(payload)
    },
    async onUpdateLinksOrder(payload) {
      await this.saveImmediately(payload)
      this.$refs.message.open('Ordningen uppdaterad')
    },
    patchUpdateData(update) {
      update.life_spec_weekly
        ? (update.life_spec_weekly = 'true')
        : (update.life_spec_weekly = null)
      update.life_spec_featured
        ? (update.life_spec_featured = 'featured')
        : (update.life_spec_featured = null)
      return update
    },
    async save() {
      try {
        if (!this.hasUnsavedUpdates) return

        this.isSaving = true
        const updates = this.patchUpdateData({ ...this.art, ...this.updates })
        await this.updateArt(updates)
        this.updates = {}

        this.$refs.message.open('Artinfo sparad')
      } catch (err) {
        throw new Error(err)
      } finally {
        this.isSaving = false
      }
    },
    async saveImmediately(payload) {
      this.isSaving = true
      await this.updateArt({ ...this.art, ...payload })
      this.isSaving = false
    },
    async onArtPublish() {
      const newState = this.art.status === 'online' ? 'offline' : 'online'
      await this.saveImmediately({ status: newState })
    },
  },
  created() {
    this.fetch()
  },
}
</script>

<style lang="less" scoped>
h1 {
  color: #fff;
}
.tabs-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .buttons {
    display: flex;
    gap: 15px;
  }
}
</style>
